import React, { useState, useEffect } from 'react';
import { userAPI, extractApiError } from '../../services/api';
import AlertBanner from '../common/AlertBanner';
import '../../styles/admin/Modal.css';

/**
 * ユーザー作成・編集モーダル
 */
const UserModal = ({ user, companies, onClose, onSaveSuccess }) => {
  const [formData, setFormData] = useState({
    display_name: '',
    email: '',
    password: '',
    company_id: '',
    role: 'general',
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [lastPayload, setLastPayload] = useState(null);

  const isEditMode = !!user;

  // 編集モードの場合、初期値を設定
  useEffect(() => {
    if (user) {
      setFormData({
        display_name: user.display_name || '',
        email: user.email || '',
        password: '', // パスワードは編集時は空
        company_id: user.company_id || '',
        role: user.role || 'general',
      });
    } else if (companies.length > 0) {
      // 新規作成時、デフォルトで最初の企業を選択
      setFormData(prev => ({ ...prev, company_id: companies[0].company_id }));
    }
  }, [user, companies]);

  // フォームの変更ハンドラ
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // エラーをクリア
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // バリデーション
  const validate = () => {
    const newErrors = {};

    if (!formData.display_name.trim()) {
      newErrors.display_name = '名前を入力してください';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'メールアドレスを入力してください';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = '有効なメールアドレスを入力してください';
    }

    // パスワードが入力されている場合は長さチェック（編集時のみ）
    if (isEditMode && formData.password && formData.password.length < 8) {
      newErrors.password = 'パスワードは8文字以上で入力してください';
    }

    if (!formData.company_id) {
      newErrors.company_id = '企業を選択してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 保存処理
  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    setSaving(true);

    try {
      // 送信データを準備
      const dataToSend = { ...formData };
      dataToSend.company_id = Number(dataToSend.company_id);

      if (isEditMode) {
        // 編集時：パスワードが空の場合は除外
        if (!dataToSend.password) {
          delete dataToSend.password;
        }

        setLastPayload({ type: 'update', id: user.user_id, data: dataToSend });
        await userAPI.updateUser(user.user_id, dataToSend);
        alert('ユーザー情報を更新しました');
      } else {
        // 新規作成時：パスワードは送らない（null）、招待メールは常に送信
        dataToSend.password = null;
        dataToSend.send_invitation = true;

        setLastPayload({ type: 'create', data: dataToSend });
        await userAPI.createUser(dataToSend);
        alert('ユーザーを作成し、招待メールを送信しました');
      }
      onSaveSuccess();
    } catch (err) {
      console.error('Failed to save user:', err);
      setServerError(extractApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleRetry = async () => {
    if (!lastPayload) return;
    setServerError(null);
    try {
      if (lastPayload.type === 'update') {
        await userAPI.updateUser(lastPayload.id, lastPayload.data);
      } else {
        await userAPI.createUser(lastPayload.data);
      }
      onSaveSuccess();
    } catch (err) {
      setServerError(extractApiError(err));
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditMode ? 'ユーザー情報の編集' : '新規ユーザーの作成'}</h2>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          <AlertBanner message={serverError} onRetry={handleRetry} onClose={() => setServerError(null)} />
          {/* 名前 */}
          <div className="form-group">
            <label>名前 *</label>
            <input
              type="text"
              value={formData.display_name}
              onChange={(e) => handleChange('display_name', e.target.value)}
              placeholder="例: 山田太郎"
              className={errors.display_name ? 'error' : ''}
            />
            {errors.display_name && (
              <span className="error-message">{errors.display_name}</span>
            )}
          </div>

          {/* メールアドレス */}
          <div className="form-group">
            <label>メールアドレス *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="example@company.com"
              className={errors.email ? 'error' : ''}
            />
            {errors.email && (
              <span className="error-message">{errors.email}</span>
            )}
          </div>

          {/* パスワード（編集時のみ表示） */}
          {isEditMode && (
            <div className="form-group">
              <label>
                パスワード (変更する場合のみ入力)
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                placeholder="8文字以上"
                className={errors.password ? 'error' : ''}
              />
              {errors.password && (
                <span className="error-message">{errors.password}</span>
              )}
            </div>
          )}

          {/* 招待メール送信の注意書き（新規作成時のみ表示） */}
          {!isEditMode && (
            <div className="info-notice">
              <small className="form-help" style={{ display: 'block', padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '4px', marginBottom: '12px' }}>
                💡 ユーザー作成後、アカウント有効化のための招待メールが自動的に送信されます。
              </small>
            </div>
          )}

          {/* 所属企業 */}
          <div className="form-group">
            <label>所属企業 *</label>
            <select
              value={formData.company_id}
              onChange={(e) => handleChange('company_id', e.target.value)}
              className={errors.company_id ? 'error' : ''}
            >
              <option value="">-- 企業を選択 --</option>
              {companies.map((company) => (
                <option key={company.company_id} value={company.company_id}>
                  {company.company_name}
                </option>
              ))}
            </select>
            {errors.company_id && (
              <span className="error-message">{errors.company_id}</span>
            )}
          </div>

          {/* 権限 */}
          <div className="form-group">
            <label>権限 *</label>
            <select
              value={formData.role}
              onChange={(e) => handleChange('role', e.target.value)}
            >
              <option value="general">一般ユーザー</option>
              <option value="company_admin">企業管理者</option>
              <option value="system_admin">システム管理者</option>
            </select>
            <small className="form-help">
              一般ユーザー: 自分の予約のみ管理可能<br />
              企業管理者: 自社の全予約を管理可能<br />
              システム管理者: すべてのデータを管理可能
            </small>
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary" disabled={saving}>
            キャンセル
          </button>
          <button onClick={handleSave} className="btn-primary" disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserModal;
